import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { API_BASE_URL } from '../api/client';

interface Game {
  id: number;
  name: string;
  description: string;
  gameType: string;
  active: boolean;
  imageUrl: string;
  createdAt: string;
  updatedAt: string;
  settings: GameSettings;
}

interface GameSettings {
  id: number;
  minPlayers: number;
  maxPlayers: number;
  cardsPerPlayer: number;
  turnTimeLimit: number;
  allowSpectators: boolean;
  allowChat: boolean;
  customRules: string;
}

export default function AdminGamesPage() {
  const queryClient = useQueryClient();
  const { token } = useAuthStore();
  const [isCreating, setIsCreating] = useState(false);
  const [editingGame, setEditingGame] = useState<number | null>(null);

  const { data: games, isLoading } = useQuery<Game[]>({
    queryKey: ['admin-games'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api/admin/games`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch games');
      return response.json();
    },
  });

  const createGameMutation = useMutation({
    mutationFn: async (gameData: any) => {
      const response = await fetch(`${API_BASE_URL}/api/admin/games`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(gameData),
      });
      if (!response.ok) throw new Error('Failed to create game');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-games'] });
      setIsCreating(false);
    },
  });

  const updateGameMutation = useMutation({
    mutationFn: async ({ gameId, gameData }: { gameId: number; gameData: any }) => {
      const response = await fetch(`${API_BASE_URL}/api/admin/games/${gameId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(gameData),
      });
      if (!response.ok) throw new Error('Failed to update game');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-games'] });
      setEditingGame(null);
    },
  });

  const deleteGameMutation = useMutation({
    mutationFn: async (gameId: number) => {
      const response = await fetch(`${API_BASE_URL}/api/admin/games/${gameId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to delete game');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-games'] });
    },
  });

  const handleCreateGame = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const gameData = {
      name: formData.get('name'),
      description: formData.get('description'),
      gameType: formData.get('gameType'),
      imageUrl: formData.get('imageUrl'),
      settings: {
        minPlayers: parseInt(formData.get('minPlayers') as string),
        maxPlayers: parseInt(formData.get('maxPlayers') as string),
        cardsPerPlayer: parseInt(formData.get('cardsPerPlayer') as string),
        turnTimeLimit: parseInt(formData.get('turnTimeLimit') as string),
        allowSpectators: formData.get('allowSpectators') === 'true',
        allowChat: formData.get('allowChat') === 'true',
        customRules: formData.get('customRules'),
      },
    };
    createGameMutation.mutate(gameData);
  };

  if (isLoading) {
    return <div className="text-white">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            Game Management
          </h1>
          <p className="mt-2 text-zinc-400">Manage games, settings, and associated decks</p>
        </div>
        <button
          onClick={() => setIsCreating(!isCreating)}
          className="btn-primary"
        >
          {isCreating ? 'Cancel' : 'Create New Game'}
        </button>
      </div>

      {isCreating && (
        <div className="card p-6">
          <h2 className="text-xl font-bold text-white mb-4">Create New Game</h2>
          <form onSubmit={handleCreateGame} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Game Name</label>
                <input name="name" type="text" required className="input-field" />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Game Type</label>
                <input name="gameType" type="text" required className="input-field" placeholder="e.g., CRYPTID_HUNTERS" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-zinc-300 mb-2">Description</label>
                <textarea name="description" rows={3} className="input-field" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-zinc-300 mb-2">Image URL</label>
                <input name="imageUrl" type="text" className="input-field" />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Min Players</label>
                <input name="minPlayers" type="number" required min="1" className="input-field" />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Max Players</label>
                <input name="maxPlayers" type="number" required min="1" className="input-field" />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Cards Per Player</label>
                <input name="cardsPerPlayer" type="number" required min="1" className="input-field" />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Turn Time Limit (seconds)</label>
                <input name="turnTimeLimit" type="number" required min="1" className="input-field" />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Allow Spectators</label>
                <select name="allowSpectators" className="input-field">
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Allow Chat</label>
                <select name="allowChat" className="input-field">
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-zinc-300 mb-2">Custom Rules (JSON)</label>
                <textarea name="customRules" rows={3} className="input-field" placeholder='{"rule": "value"}' />
              </div>
            </div>
            <button type="submit" className="btn-primary w-full">Create Game</button>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {games?.map((game) => (
          <div key={game.id} className="card p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-white">{game.name}</h3>
                <p className="text-sm text-zinc-400 mt-1">{game.gameType}</p>
              </div>
              <span
                className={`px-3 py-1 rounded text-xs font-semibold ${
                  game.active
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-red-500/20 text-red-400'
                }`}
              >
                {game.active ? 'Active' : 'Inactive'}
              </span>
            </div>

            <p className="text-zinc-300 mb-4">{game.description}</p>

            <div className="bg-white/5 rounded-lg p-4 mb-4 space-y-2">
              <h4 className="text-sm font-semibold text-white mb-2">Game Settings</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-zinc-400">Players:</div>
                <div className="text-zinc-200">{game.settings.minPlayers} - {game.settings.maxPlayers}</div>
                <div className="text-zinc-400">Cards Per Player:</div>
                <div className="text-zinc-200">{game.settings.cardsPerPlayer}</div>
                <div className="text-zinc-400">Turn Limit:</div>
                <div className="text-zinc-200">{game.settings.turnTimeLimit}s</div>
                <div className="text-zinc-400">Spectators:</div>
                <div className="text-zinc-200">{game.settings.allowSpectators ? 'Yes' : 'No'}</div>
                <div className="text-zinc-400">Chat:</div>
                <div className="text-zinc-200">{game.settings.allowChat ? 'Yes' : 'No'}</div>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  if (window.confirm(`Are you sure you want to delete ${game.name}?`)) {
                    deleteGameMutation.mutate(game.id);
                  }
                }}
                className="btn-secondary flex-1"
              >
                Delete
              </button>
              <button
                onClick={() => {
                  updateGameMutation.mutate({
                    gameId: game.id,
                    gameData: { active: !game.active },
                  });
                }}
                className="btn-primary flex-1"
              >
                {game.active ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

